import { NumberDate, Timestamp } from '@lemon/core/model/common'

/** Lähdejärjestelmä, josta CSV-tiedosto on peräisin */
export type AlkusaldoLahdejarjestelma = 'netvisor' | 'procountor'

/** Virhekoodit alkusaldo-operaatioista. Frontend tulkkaa nämä käyttäjälle näytettäviksi teksteiksi. */
export enum AlkusaldoVirhekoodi {
  METADATA_EI_LOYDY = 'metadata-ei-loydy',
  TIEDOSTOA_EI_LOYDY = 'tiedostoa-ei-loydy',
  CSV_OTSIKKORIVI_PUUTTUU = 'csv-otsikkorivi-puuttuu',
  CSV_SARAKKEET_PUUTTUVAT = 'csv-sarakkeet-puuttuvat',
  CSV_EI_VIENTEJA = 'csv-ei-vienteja',
  EI_MAPPAUKSIA = 'ei-mappauksia',
  PUUTTUVIA_MAPPAUKSIA = 'puuttuvia-mappauksia',
  TILIKAUTTA_EI_LOYDY = 'tilikautta-ei-loydy',
  TUNTEMATON_VIRHE = 'tuntematon-virhe',
  SALDO_PUUTTUU = 'saldo-puuttuu',
  ALKUSALDOJA_EI_TUETA = 'alkusaldoja-ei-tueta',
  TIEDOSTOT_PAALLEKKAISIA = 'tiedostot-paallekkaisia',
  LAHDEJARJESTELMA_EPAVARMA = 'lahdejarjestelma-epavarma',
}

/** Sisäinen tietomalli: yksi kirjanpidon vienti CSV-tiedostosta */
export interface AlkusaldoTuontivienti {
  /** Alkuperäinen rivinumero CSV-tiedostossa (1-pohjainen, otsikkorivi ei lasketa) */
  rivinumero: number
  /** Indeksi kronologisesti järjestettyyn tiedostolistaan */
  tiedostoIndeksi: number
  /** Tilinumero lähdejärjestelmässä */
  lahdetilinumero: string
  /** Tilin nimi lähdejärjestelmässä */
  lahdetilinimi: string
  /** Päivämäärä (NumberDate yymmdd) */
  pvm: NumberDate
  /** Debet */
  debet: number
  /** Kredit */
  kredit: number
  /** Saldo */
  saldo: number
  /** ALV-prosentti (esim. 0, 10, 24) */
  alvProsentti: number
  /** ALV euroissa */
  alvEur: number
  /** ALV-tunnus lähdejärjestelmässä (esim. "KOOS", "-") */
  alvTunnus: string
  /** Selite */
  selite: string
}

/** Yksi tili joka löytyy CSV-tiedostosta (aggregoitu tilikohtaisesti) */
export interface AlkusaldoLahdetili {
  /** Tilinumero lähdejärjestelmässä */
  numero: string
  /** Tilin nimi lähdejärjestelmässä */
  nimi: string
}

/** Yhden tilin mappaus: lähdetili -> meidän tilikartan tili */
export interface AlkusaldoTiliMappaus {
  /** Tilinumero lähdejärjestelmässä */
  lahdetilinumero: string
  /** Meidän tilikartan tilinumero */
  kohdetilinumero: string
}

/** Yksi tuontiin valittu tiedosto hallintokansiossa */
export interface AlkusaldoTuontitiedosto {
  /** Arkistotiedoston metadata-avain (ArkistoFile.avain) */
  arkistoTiedostonAvain: string
  /** Tiedoston nimi (näyttöä varten) */
  tiedostonNimi: string
}

/** Pyyntö CSV-tiedostojen parsimiseksi */
export interface AlkusaldoCsvParsePyynto {
  asiakasAvain: string
  /** Arkistotiedostojen metadata-avaimet (ArkistoFile.avain) */
  arkistoTiedostonAvaimet: string[]
  /** Jos puuttuu, backend tunnistaa lähdejärjestelmän tiedostojen sisällöstä. */
  lahdejarjestelma?: AlkusaldoLahdejarjestelma
}

/** Vastaus CSV:n parsinnasta */
export interface AlkusaldoCsvParseVastaus {
  onnistui: boolean
  virhekoodi?: AlkusaldoVirhekoodi
  /** Parsitut viennit */
  viennit?: AlkusaldoTuontivienti[]
  /** Löydetyt tilit (uniikit) */
  lahdetilit?: AlkusaldoLahdetili[]
  /** Tiedostonimet tiedostoIndeksi-järjestyksessä */
  tiedostot?: string[]
  /** Parsinnassa käytetty lähdejärjestelmä (pyynnössä annettu tai tiedostoista tunnistettu) */
  lahdejarjestelma?: AlkusaldoLahdejarjestelma
  virhekonteksti?: {
    paallekkaisetTiedostot?: string[]
  }
}

/** Pyyntö kirjausten luomiseksi. Mappaukset, tiedostotiedot ja lähdejärjestelmä
 *  luetaan backendissä AlkusaldoImportDocument-dokumentista. */
export interface AlkusaldoKirjaustenLuontiPyynto {
  asiakasAvain: string
  /** Alkupäivämäärä (NumberDate yyyymmdd). Jos tyhjä, ei alarajausta. */
  alkuPvm?: NumberDate
  /** Loppupäivämäärä (NumberDate yyyymmdd). Jos tyhjä, ei ylärajausta. */
  loppuPvm?: NumberDate
  /** Luodaanko alkusaldokirjaukset aineiston ensimmäisen tilikauden ensimmäiselle päivälle */
  luoAlkusaldokirjaukset?: boolean
}

/** Vastaus kirjausten luonnista */
export interface AlkusaldoKirjaustenLuontiVastaus {
  onnistui: boolean
  virhekoodi?: AlkusaldoVirhekoodi
  luodutKirjaukset?: number
  luodutViennit?: number
  /** Tilikausiavaimet, joille lippu tilikaudenVaihteenKirjauksetEstetty asetettiin tässä tuonnissa */
  vaihdekirjauksetEstettyTilikaudet?: string[]
  /** Tilikauden tulos -tilille (2370) osoitettujen vientien määrä, jotka jätettiin tuomatta */
  ohitetutTilikaudenTuloksenViennit?: number
  virhekonteksti?: {
    vienti?: AlkusaldoTuontivienti
    paallekkaisetTiedostot?: string[]
  }
}

/** Pyyntö kaikkien alkusaldokirjausten poistamiseksi */
export interface AlkusaldoPoistaPyynto {
  asiakasAvain: string
}

/** Vastaus alkusaldokirjausten poistosta */
export interface AlkusaldoPoistaVastaus {
  onnistui: boolean
  virhekoodi?: AlkusaldoVirhekoodi
  poistetutKirjaukset?: number
}

/** Historia-dokumentin tyyppi */
export enum AlkusaldoHistoriaTyyppi {
  /** Käyttäjä tallensi mappauksen ilman kirjausten luontia */
  VALITALLENNUS = 'valitallennus',
  /** Varsinainen tuonti joka tuotti kirjauksia */
  TUONTI = 'tuonti',
}

/** Asiakkaan tallennettu import-dokumentti (edellisen tuonnin mappaukset) */
// Firestore path: asiakkaat/{asiakasAvain}/muut/alkusaldo-import
export interface AlkusaldoImportDocument {
  /** Viimeisin tuontiaika */
  tuotu: Timestamp
  /** Lähdejärjestelmä. Puuttuu, jos dokumentti on tallennettu ennen tiedostojen lukemista eikä käyttäjä valinnut järjestelmää käsin. */
  lahdejarjestelma?: AlkusaldoLahdejarjestelma
  /** Tallennetut mappaukset uudelleenkäyttöä varten */
  mappaukset: AlkusaldoTiliMappaus[]
  /** Tuojan uid */
  tuoja: string
  /** Mistä tiedostoista tuotiin */
  tiedostot: AlkusaldoTuontitiedosto[]
  /** Kirjanpitäjän viimeisin valinta alkusaldokirjausten luonnista (muistetaan kuten mappaukset) */
  luoAlkusaldokirjaukset?: boolean
  /** Tilikausiavaimet, joille tuonti on asettanut tilikaudenVaihteenKirjauksetEstetty-lipun (alkusaldoPoista purkaa nämä) */
  vaihdekirjauksetEstettyTilikaudet?: string[]
}

export interface AlkusaldoImportHistoriaDocument extends AlkusaldoImportDocument {
  /** Tallennuksen tyyppi */
  tyyppi: AlkusaldoHistoriaTyyppi
}
